/**
 * Maintenance mode.
 *
 * Public (unauthenticated) read of the maintenance flag: the whole public site
 * needs it, so it must be readable without a session. Only the three
 * maintenance columns are ever returned, plus the caller's own IP.
 */
import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";

function clientIp(): string {
  const cf = getRequestHeader("cf-connecting-ip");
  if (cf) return cf.trim();
  const real = getRequestHeader("x-real-ip");
  if (real) return real.trim();
  const fwd = getRequestHeader("x-forwarded-for");
  if (fwd) return (fwd.split(",")[0] ?? "").trim();
  return "";
}

/** Exact match, or prefix wildcard like `192.168.1.*` / `10.*`. */
export function ipAllowed(ip: string, allowed: string[]): boolean {
  if (!ip) return false;
  return allowed.some((raw) => {
    const rule = raw.trim();
    if (!rule) return false;
    if (rule.endsWith("*")) return ip.startsWith(rule.slice(0, -1));
    return rule === ip;
  });
}

export const getMaintenanceState = createServerFn({ method: "GET" }).handler(async () => {
  const ip = clientIp();
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("admin_settings")
      .select("maintenance_enabled, maintenance_message, maintenance_allowed_ips")
      .eq("singleton", true)
      .single();
    if (error || !data) return { blocked: false, message: "", ip };
    if (!data.maintenance_enabled) return { blocked: false, message: "", ip };
    const allowed = ipAllowed(ip, data.maintenance_allowed_ips ?? []);
    return { blocked: !allowed, message: data.maintenance_message ?? "", ip };
  } catch {
    return { blocked: false, message: "", ip };
  }
});
